import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { gray } from '@avangard/ui/colors';
import {
    DatepickerTextField,
    DatepickerSinglePicker,
    TextField,
    Autocomplete,
} from '@avangard/ui/core';

import { FormStyled } from '@modules/layout/styled';
import { useRegions, useLazyLocations } from '@modules/shared/hooks';
import { gendersOptions } from '@modules/user/constants';

import type { OnDateChangeProps } from '@avangard/ui/core';
import type { RegionEntity, LocationEntity } from '@modules/shared/entities';
import type { StudentEntity } from '@modules/students/entities';
import type { UpdateStudentFormValues } from '@modules/students/requests';

type StudentSummaryEditFormGroupProps = {
    student: StudentEntity;
};

const QrCodeWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 150px;
    height: 150px;

    > img {
        width: 100%;
        height: 100%;
        padding: 8px;
        border: 1px solid ${gray[40]};
    }
`;

const StudentSummaryEditFormGroup = (
    props: StudentSummaryEditFormGroupProps,
): React.ReactElement => {
    const { student } = props;

    const { t } = useTranslation(['common', 'students']);

    const { values, errors, handleChange, handleBlur, setFieldValue } =
        useFormikContext<UpdateStudentFormValues>();

    const { regions, loading: regionsLoading } = useRegions({
        variables: { args: { filter: { all: true } } },
    });

    const [getLocations, { locations, count: locationsCount, loading: locationsLoading }] =
        useLazyLocations();

    React.useEffect(() => {
        if (!values.summary.region) {
            return;
        }

        getLocations({
            variables: { regionId: values.summary.region.id },
        });
    }, [values.summary.region]);

    const getOptionLabel =
        (field: string, withTranslate?: boolean) =>
        (option: any | null): string => {
            if (!option?.[field]) {
                return '-';
            }

            if (withTranslate) {
                return t(option[field]);
            }

            return option[field];
        };

    const getGenderOptionValue = (option?: string | null) => {
        const idx = gendersOptions.findIndex(gender => gender.key === option);

        if (idx > -1) {
            return gendersOptions[idx];
        }

        return null;
    };

    const handleChangeAutocomplete =
        (field: string) => (_: React.ChangeEvent<unknown>, option: any | null) => {
            setFieldValue(field, option);

            if (field === 'summary.region') {
                setFieldValue('summary.location', null);
            }
        };

    const handleChangeDate = (data: OnDateChangeProps) =>
        setFieldValue('summary.birthdate', data.date);

    const handleChangeGender = (_: any, option: AutocompleteOption | null) =>
        setFieldValue('summary.gender', option?.key);

    return (
        <>
            <FormStyled.FormColumn>
                <FormStyled.FormColumnTitle>
                    {t('students:tabs.summary')}
                </FormStyled.FormColumnTitle>

                <TextField
                    required
                    fullWidth
                    id='summary.fullName'
                    label={t('common:forms.labels.full_name')}
                    placeholder={t('common:forms.placeholder.full_name_long') ?? ''}
                    value={values.summary.fullName}
                    error={!!errors.summary?.fullName}
                    helperText={errors.summary?.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <Autocomplete<AutocompleteOption | null, false, false, false>
                    required
                    id='gender'
                    label={t('common:forms.labels.gender') ?? ''}
                    placeholder={t('common:forms.placeholder.choose_gender') ?? ''}
                    options={gendersOptions}
                    value={getGenderOptionValue(values.summary.gender)}
                    error={!!errors.summary?.gender}
                    helperText={errors.summary?.gender}
                    onChange={handleChangeGender}
                    getOptionLabel={getOptionLabel('value', true)}
                    getOptionSelected={(option, value) => option?.key === value?.key}
                />

                <DatepickerSinglePicker
                    date={values.summary.birthdate}
                    onDateChange={handleChangeDate}
                >
                    {datepickerProps => {
                        const { inputProps, inputComponent } = datepickerProps;

                        return (
                            <DatepickerTextField
                                required
                                id='summary.birthdate'
                                label={t('common:forms.labels.birthdate') ?? ''}
                                placeholder={t('common:forms.labels.date') ?? ''}
                                error={!!errors.summary?.birthdate}
                                helperText={errors.summary?.birthdate}
                                InputProps={{ inputComponent }}
                                {...inputProps}
                            />
                        );
                    }}
                </DatepickerSinglePicker>

                <Autocomplete<RegionEntity | null, false, false, false>
                    fullWidth
                    id='summary.region'
                    label={t('common:forms.labels.region_residence') ?? ''}
                    placeholder={t('common:forms.placeholder.choose_region_residence') ?? ''}
                    options={regions}
                    value={values.summary.region}
                    error={!!errors.summary?.region}
                    helperText={errors.summary?.region}
                    disabled={regionsLoading}
                    getOptionLabel={getOptionLabel('name')}
                    onChange={handleChangeAutocomplete('summary.region')}
                />

                <Autocomplete<LocationEntity | null, false, false, false>
                    fullWidth
                    id='summary.location'
                    label={t('common:forms.labels.location_residence') ?? ''}
                    placeholder={t('common:forms.placeholder.choose_location_residence') ?? ''}
                    options={locations}
                    value={values.summary.location}
                    error={!!errors.summary?.location}
                    helperText={errors.summary?.location}
                    disabled={regionsLoading || locationsCount === 0 || locationsLoading}
                    getOptionLabel={getOptionLabel('name')}
                    onChange={handleChangeAutocomplete('summary.location')}
                />

                <TextField
                    fullWidth
                    id='summary.address'
                    label={t('students:form.labels.address')}
                    placeholder={t('students:form.placeholder.typing_address') ?? ''}
                    value={values.summary.address}
                    error={!!errors.summary?.address}
                    helperText={errors.summary?.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <FormStyled.FormRow>
                    <FormStyled.FormColumnTitle>
                        {t('common:forms.titles.qr_code')}
                    </FormStyled.FormColumnTitle>
                    <QrCodeWrap>
                        <img src={student.qrCodeUrl} alt='' />
                    </QrCodeWrap>
                </FormStyled.FormRow>
            </FormStyled.FormColumn>

            <FormStyled.FormColumn>
                <FormStyled.FormColumnTitle>
                    {t('common:forms.titles.contacts')}
                </FormStyled.FormColumnTitle>

                <TextField
                    fullWidth
                    id='summary.phone'
                    label={t('common:forms.labels.phone') ?? ''}
                    placeholder={t('common:forms.placeholder.phone') ?? ''}
                    value={values.summary.phone}
                    error={!!errors.summary?.phone}
                    helperText={errors.summary?.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    required
                    fullWidth
                    id='summary.email'
                    label={t('common:forms.labels.email')}
                    placeholder={t('common:forms.placeholder.email') ?? ''}
                    value={values.summary.email}
                    error={!!errors.summary?.email}
                    helperText={errors.summary?.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    fullWidth
                    id='summary.vk'
                    label={t('common:forms.labels.vk')}
                    placeholder={t('common:forms.placeholder.vk') ?? ''}
                    value={values.summary.vk}
                    error={!!errors.summary?.vk}
                    helperText={errors.summary?.vk}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </FormStyled.FormColumn>
        </>
    );
};

export { StudentSummaryEditFormGroup };
